import type { Challenge } from '@/domain/workout'
import type { Progress } from '@/domain/session'
import { sessionsOfWeek } from '@/domain/session'
import { cx } from '@/lib/cx'
import { hasStarted } from '@/lib/weeks'
import styles from './panel.module.css'

interface WeekPickerProps {
  challenge: Challenge
  progress: Progress
  activeWeek: number
  onSelect: (weekNumber: number) => void
}

/**
 * Selector de semanas. Ninguna está bloqueada: se puede entrenar la que sea
 * y en el orden que sea, aunque no se haya hecho la anterior.
 */
export function WeekPicker({ challenge, progress, activeWeek, onSelect }: WeekPickerProps) {
  return (
    <nav className={styles.weeks} aria-label="Semanas del reto">
      {challenge.weeks.map((week) => {
        const done = sessionsOfWeek(progress, week.number).length
        const complete = done >= challenge.sessionsPerWeek
        const started = hasStarted(week)

        const state = cx(
          week.number === activeWeek && styles.weekActive,
          complete && styles.weekDone,
          !started && styles.weekAhead,
        )

        // Una semana cerrada anuncia el día en que se abre, no su estado.
        const label = !started
          ? `${week.dateLabel.split(' ')[0] ?? ''} ago`
          : complete
            ? 'Completa'
            : done > 0
              ? `${done} de ${challenge.sessionsPerWeek}`
              : 'Abierta'

        return (
          <button
            key={week.number}
            type="button"
            className={cx(styles.week, state)}
            aria-current={week.number === activeWeek ? 'true' : undefined}
            onClick={() => onSelect(week.number)}
          >
            <b>{String(week.number).padStart(2, '0')}</b>
            <span>{label}</span>
          </button>
        )
      })}
    </nav>
  )
}
